import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import * as Yup from "yup";
import styled from "@emotion/styled";
import { Formik } from "formik";
import axios from "axios";
import {
  Grid,
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Paper as MuiPaper,
  Card as MuiCard,
  CardContent,
  CircularProgress,
  Button as MuiButton,
  TextField as MuiTextField,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
  Tooltip,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Info as InfoIcon } from "@mui/icons-material";
import { spacing } from "@mui/system";
import Select from "./SelectBox";
import ConfirmDialog from "./ConfirmDialog";
import { GlobalContext } from "../contexts/GlobalContext";
import { usePermissions } from "../hooks/usePermissions";
import { CAPABILITIES } from "../auth/permissions";
import {
  EXCISE_PRODUCT_TYPE,
  filterCountriesForProduct,
  getCountryProductHelperText,
  isCountryValidForProduct,
} from "../utils/countryProductUtils";

const Card = styled(MuiCard)(spacing);
const Paper = styled(MuiPaper)(spacing);
const TextField = styled(MuiTextField)(spacing);
const Button = styled(MuiButton)(spacing);

// Helper function to get user's locale decimal separator
const getDecimalSeparator = () => {
  try {
    const numberFormat = new Intl.NumberFormat(navigator.language || "en-US");
    const parts = numberFormat.formatToParts(1.5);
    const decimalPart = parts.find((part) => part.type === "decimal");
    return decimalPart ? decimalPart.value : ".";
  } catch (error) {
    return ".";
  }
};

// Helper function to convert comma to dot and parse to float
const parseDecimalValue = (value) => {
  if (!value || value === "") return 0;
  const normalizedValue = String(value).replace(",", ".");
  return parseFloat(normalizedValue) || 0;
};

// Helper function to format value with 2 decimals using user's locale separator
const formatWithTwoDecimals = (value) => {
  if (!value && value !== 0) {
    const separator = getDecimalSeparator();
    return `0${separator}00`;
  }
  const numValue = typeof value === "string" ? parseDecimalValue(value) : value;
  const formatted = numValue.toFixed(2);
  const separator = getDecimalSeparator();
  return separator === "," ? formatted.replace(".", ",") : formatted;
};

// Helper function to format price with euro sign
const formatPriceWithEuro = (value) => {
  return `€ ${formatWithTwoDecimals(value)}`;
};

const initialValues = {
  productType: "",
  country: "",
  volumePeriod: "",
  volume: "",
  rate: "",
  rateId: "",
  tieredPrice: "",
  priceAdjustmentType: "none",
  priceAdjustmentPercent: formatWithTwoDecimals(0),
  vf: "",
  preFinancePeriodType: "",
};

const PRE_FINANCE_PERIOD_MONTHLY = 1;
const PRE_FINANCE_PERIOD_QUARTERLY = 2;

const hasPositiveVf = (vf) => {
  if (vf === null || vf === undefined || vf === "") return false;
  const parsed = typeof vf === "number" ? vf : parseDecimalValue(vf);
  return Number.isFinite(parsed) && parsed > 0;
};

const validationSchema = Yup.object().shape({
  productType: Yup.number().required("Required").oneOf([1, 2], "Must be VAT (1) or Excise (2)"),
  country: Yup.number()
    .required("Required")
    .test(
      "country-product-match",
      "Selected country does not match the product type",
      function validateCountryForProduct(value) {
        if (!value || !this.parent.productType) {
          return true;
        }

        return isCountryValidForProduct(value, this.parent.productType);
      }
    ),
  volumePeriod: Yup.number().required("Required").oneOf([1, 2, 3], "Must be Month (1), Quarter (2), or Year (3)"),
  volume: Yup.mixed()
    .transform((value, originalValue) => {
      if (originalValue === "" || originalValue === null || originalValue === undefined) return undefined;
      if (typeof originalValue === "number" && !isNaN(originalValue)) return originalValue;
      const stringValue = String(originalValue).trim();
      if (stringValue === "") return undefined;
      const normalized = stringValue.replace(",", ".");
      const parsed = parseFloat(normalized);
      return isNaN(parsed) ? undefined : parsed;
    })
    .test("is-number", "Volume must be a number", (value) => value !== undefined && !isNaN(value))
    .required("Required")
    .test("is-positive", "Volume must be positive", (value) => value >= 0),
  rate: Yup.mixed()
    .transform((value, originalValue) => {
      if (originalValue === "" || originalValue === null || originalValue === undefined) return undefined;
      if (typeof originalValue === "number" && !isNaN(originalValue)) return originalValue;
      const stringValue = String(originalValue).trim();
      if (stringValue === "") return undefined;
      const normalized = stringValue.replace(",", ".");
      const parsed = parseFloat(normalized);
      return isNaN(parsed) ? undefined : parsed;
    })
    .test("is-number", "Rate must be a number", (value) => value !== undefined && !isNaN(value))
    .required("Required")
    .test("is-positive", "Rate must be positive", (value) => value >= 0),
  tieredPrice: Yup.mixed()
    .transform((value, originalValue) => {
      if (originalValue === "" || originalValue === null || originalValue === undefined) return undefined;
      if (typeof originalValue === "number" && !isNaN(originalValue)) return originalValue;
      const stringValue = String(originalValue).trim();
      if (stringValue === "") return undefined;
      const normalized = stringValue.replace(",", ".");
      const parsed = parseFloat(normalized);
      return isNaN(parsed) ? undefined : parsed;
    })
    .test("is-number", "Tier Price must be a number", (value) => value !== undefined && !isNaN(value))
    .required("Required")
    .test("is-positive", "Tier Price must be positive", (value) => value >= 0),
  priceAdjustmentPercent: Yup.mixed()
    .transform((value, originalValue) => {
      if (originalValue === "" || originalValue === null || originalValue === undefined) return undefined;
      if (typeof originalValue === "number" && !isNaN(originalValue)) return originalValue;
      const stringValue = String(originalValue).trim();
      if (stringValue === "") return undefined;
      const normalized = stringValue.replace(",", ".");
      const parsed = parseFloat(normalized);
      return isNaN(parsed) ? undefined : parsed;
    })
    .test("is-number", "Adjustment must be a number", (value) => value !== undefined && !isNaN(value))
    .required("Required")
    .test("min", "Adjustment must be between 0 and 100", (value) => value >= 0)
    .test("max", "Adjustment must be between 0 and 100", (value) => value <= 100),
  vf: Yup.mixed()
    .transform((value, originalValue) => {
      // Allow empty, null, or undefined - return null for optional field
      if (originalValue === "" || originalValue === null || originalValue === undefined) return null;
      if (typeof originalValue === "number" && !isNaN(originalValue)) return originalValue;
      const stringValue = String(originalValue).trim();
      // Empty string after trim should also return null
      if (stringValue === "") return null;
      const normalized = stringValue.replace(",", ".");
      const parsed = parseFloat(normalized);
      // Return null if parsing fails (invalid input)
      return isNaN(parsed) ? null : parsed;
    })
    .nullable()
    .test("is-number", "VF must be a number", (value) => {
      // Allow null (empty/optional), or must be a valid number
      if (value === null || value === undefined) return true;
      return !isNaN(value) && typeof value === "number";
    })
    .test("min", "VF must be between 0 and 100", (value) => {
      // Only validate if value is provided
      if (value === null || value === undefined) return true;
      return value >= 0;
    })
    .test("max", "VF must be between 0 and 100", (value) => {
      // Only validate if value is provided
      if (value === null || value === undefined) return true;
      return value <= 100;
    }),
  preFinancePeriodType: Yup.mixed()
    .nullable()
    .when("vf", {
      is: (vf) => hasPositiveVf(vf),
      then: (schema) =>
        schema
          .transform((value, originalValue) => {
            if (originalValue === "" || originalValue === null || originalValue === undefined) return undefined;
            const parsed = parseInt(originalValue, 10);
            return Number.isNaN(parsed) ? undefined : parsed;
          })
          .required("Required when VF is set")
          .oneOf([PRE_FINANCE_PERIOD_MONTHLY, PRE_FINANCE_PERIOD_QUARTERLY], "Must be Monthly or Quarterly"),
      otherwise: (schema) => schema.nullable(),
    }),
});

const AddCustomerPricingAgreement = ({
  customerId,
  refreshTable,
  countryData,
  setNotify,
  editDialog,
  setEditDialog,
  createDialog,
  setCreateDialog,
}) => {
  const { globalData } = useContext(GlobalContext);
  const { hasPermission } = usePermissions();
  const canWritePricing = hasPermission(CAPABILITIES.customersPricing, "W");
  const [open, setOpen] = useState(false);
  const [refundRates, setRefundRates] = useState([]);
  const [loadingRates, setLoadingRates] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [editModeRateId, setEditModeRateId] = useState("");
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
    onConfirm: () => {},
  });

  const fetchRefundRates = useCallback(async (countryId, productId) => {
    if (!countryId || !productId) {
      setRefundRates([]);
      return;
    }

    setLoadingRates(true);
    try {
      const response = await axios.get(`/api/v1/refundrates/country/${countryId}`);
      const fetchedRates = response.data || [];
      const filteredRates = fetchedRates.filter((rate) => rate.productId === productId);
      
      // Sort by fromRate ascending
      const sortedRates = [...filteredRates].sort((a, b) => {
        const fromRateA = parseFloat(String(a.fromRate).replace(",", ".")) || 0;
        const fromRateB = parseFloat(String(b.fromRate).replace(",", ".")) || 0;
        return fromRateA - fromRateB;
      });

      setRefundRates(sortedRates);
    } catch (error) {
      console.error("Error fetching refund rates:", error);
      setRefundRates([]);
    } finally {
      setLoadingRates(false);
    }
  }, []);

  useEffect(() => {
    if (editDialog.isOpen && editDialog.agreement) {
      setOpen(true);
      // Set initial country and product for edit mode
      setSelectedCountry(editDialog.agreement.country);
      setSelectedProduct(editDialog.agreement.productType);
      // Fetch refund rates for edit mode
      if (editDialog.agreement.country && editDialog.agreement.productType) {
        fetchRefundRates(editDialog.agreement.country, editDialog.agreement.productType);
      }
    }
  }, [editDialog, fetchRefundRates]);

  useEffect(() => {
    if (createDialog?.isOpen && createDialog?.draft) {
      setOpen(true);
      setSelectedCountry(createDialog.draft.country || null);
      setSelectedProduct(createDialog.draft.productType || null);
      if (createDialog.draft.country && createDialog.draft.productType) {
        fetchRefundRates(createDialog.draft.country, createDialog.draft.productType);
      }
    }
  }, [createDialog, fetchRefundRates]);

  // Update rateId when refund rates are loaded and we're in edit mode
  const handleCountryChange = (countryId) => {
    setSelectedCountry(countryId);
    if (selectedProduct) {
      fetchRefundRates(countryId, selectedProduct);
    }
  };

  const handleProductChange = (productId) => {
    setSelectedProduct(productId);
    if (selectedCountry) {
      fetchRefundRates(selectedCountry, productId);
    }
  };

  // Find the refund rate that matches the volume (volume must be between fromRate and toRate)
  // RefundRates are monthly-based, so we need to convert volume to monthly first based on volumePeriod
  const findRateByVolume = (volume, volumePeriod) => {
    if (!volume || refundRates.length === 0 || !volumePeriod) return null;
    const volumeNum = parseDecimalValue(volume);
    
    // Convert volume to monthly based on volumePeriod
    // 1 = Month, 2 = Quarter, 3 = Year
    let monthlyVolume = volumeNum;
    switch (Number(volumePeriod)) {
      case 2: // Quarter
        monthlyVolume = volumeNum / 3;
        break;
      case 3: // Year
        monthlyVolume = volumeNum / 12;
        break;
      case 1: // Month
      default:
        monthlyVolume = volumeNum;
        break;
    }
    
    const matchingRate = refundRates.find((r) => {
      const fromRate = parseDecimalValue(r.fromRate);
      const toRate = parseDecimalValue(r.toRate);
      return monthlyVolume >= fromRate && monthlyVolume <= toRate;
    });
    return matchingRate || null;
  };

  // Calculate tier price based on volumePeriod
  const calculateTierPrice = (volumePeriod, refundRate) => {
    if (!refundRate) return 0;
    
    const monthFee = parseDecimalValue(refundRate.monthFee || 0);
    
    switch (volumePeriod) {
      case 1: // Month
        return monthFee;
      case 2: // Quarter
        // Calculate as monthFee * 3
        return monthFee * 3;
      case 3: // Year
        // Calculate as monthFee * 12
        return monthFee * 12;
      default:
        return 0;
    }
  };

  const calculatePrice = (tieredPrice, priceAdjustmentPercent) => {
    const tieredPriceValue = parseDecimalValue(tieredPrice);
    const adjustmentPercent = parseDecimalValue(priceAdjustmentPercent);

    // Calculation: Tier Price × (1 + Adjustment%)
    const calculated = tieredPriceValue * (1 + adjustmentPercent / 100);
    return calculated;
  };

  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    if (!globalData?.userId) {
      if (setNotify) {
        setNotify({
          isOpen: true,
          message: "User information not available",
          type: "error",
        });
      }
      setSubmitting(false);
      return;
    }

    try {
      const rawAdjustmentPercent = parseDecimalValue(values.priceAdjustmentPercent);
      const signedAdjustmentPercent =
        values.priceAdjustmentType === "none"
          ? 0
          : values.priceAdjustmentType === "discount"
          ? -rawAdjustmentPercent
          : rawAdjustmentPercent;

      const vfValue = values.vf ? parseDecimalValue(values.vf) : null;
      const hasVf = hasPositiveVf(values.vf);
      const preFinancePeriodType = hasVf ? parseInt(values.preFinancePeriodType, 10) : null;

      const payload = {
        entityId: parseInt(customerId),
        productType: parseInt(values.productType),
        country: parseInt(values.country),
        tieredPrice: parseDecimalValue(values.tieredPrice),
        priceAdjustmentPercent: signedAdjustmentPercent,
        rate: parseDecimalValue(values.rate),
        vf: hasVf ? vfValue : null,
        preFinancePeriodType: hasVf && !Number.isNaN(preFinancePeriodType) ? preFinancePeriodType : null,
        volume: parseDecimalValue(values.volume),
        volumePeriod: parseInt(values.volumePeriod),
        createdBy: globalData.userId,
      };

      if (editDialog.agreement) {
        // Update existing agreement
        await axios.put(`/api/v1/customerpricingagreements/${editDialog.agreement.id}`, {
          ...payload,
          lastModifiedBy: globalData.userId,
        });
        if (setNotify) {
          setNotify({
            isOpen: true,
            message: "Pricing agreement updated successfully",
            type: "success",
          });
        }
      } else {
        // Create new agreement
        await axios.post("/api/v1/customerpricingagreements", payload);
        if (setNotify) {
          setNotify({
            isOpen: true,
            message: "Pricing agreement added successfully",
            type: "success",
          });
        }
      }

      resetForm();
      setOpen(false);
      setEditDialog({ isOpen: false, agreement: null });
      if (setCreateDialog) {
        setCreateDialog({ isOpen: false, draft: null });
      }
      setRefundRates([]);
      setSelectedCountry(null);
      setSelectedProduct(null);
      refreshTable();
    } catch (error) {
      console.error("Error saving pricing agreement:", error);
      const errorMsg =
        error.response?.data?.message ||
        (typeof error.response?.data === "string" ? error.response.data : "") ||
        error.message ||
        "Unknown error occurred";
      if (setNotify) {
        setNotify({
          isOpen: true,
          message: "Error: " + errorMsg,
          type: "error",
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setEditDialog({ isOpen: false, agreement: null });
    if (setCreateDialog) {
      setCreateDialog({ isOpen: false, draft: null });
    }
    setRefundRates([]);
    setSelectedCountry(null);
    setSelectedProduct(null);
    setEditModeRateId("");
  };

  const countryOptions = useMemo(() => {
    const filteredCountries = filterCountriesForProduct(countryData, selectedProduct);
    return filteredCountries.map((country) => ({
      key: country.countryId,
      value: `${country.countryCode} - ${country.countryName}`,
    }));
  }, [countryData, selectedProduct]);

  const productOptions = [
    { key: 1, value: "VAT" },
    { key: 2, value: "Excise" },
  ];

  const volumePeriodOptions = [
    { key: 1, value: "Month" },
    { key: 2, value: "Quarter" },
    { key: 3, value: "Year" },
  ];

  // Find the refund rate ID that matches the current rate value (for edit mode)
  const findRateIdByValue = useCallback((rateValue) => {
    if (!rateValue || refundRates.length === 0) return "";
    const rateNum = parseDecimalValue(rateValue);
    const matchingRate = refundRates.find((r) => {
      const fromRate = parseDecimalValue(r.fromRate);
      return Math.abs(fromRate - rateNum) < 0.01; // Allow small floating point differences
    });
    return matchingRate ? matchingRate.id : "";
  }, [refundRates]);

  useEffect(() => {
    if (editDialog.agreement && refundRates.length > 0 && open) {
      const rateId = findRateIdByValue(editDialog.agreement.rate);
      setEditModeRateId(rateId || "");
    } else {
      setEditModeRateId("");
    }
  }, [refundRates, editDialog.agreement, open, findRateIdByValue]);

  // Memoize form values so they update when refundRates are loaded in edit mode
  const formInitialValues = useMemo(() => {
    if (editDialog.agreement) {
      const rateValue = formatWithTwoDecimals(editDialog.agreement.rate);
      const rawAdjustmentPercent =
        editDialog.agreement.priceAdjustmentPercent ?? editDialog.agreement.discount ?? 0;
      const priceAdjustmentType =
        rawAdjustmentPercent === 0 ? "none" : rawAdjustmentPercent < 0 ? "discount" : "surcharge";
      const priceAdjustmentPercent = formatWithTwoDecimals(Math.abs(rawAdjustmentPercent));
      // Calculate rateId if refundRates are available, otherwise use editModeRateId state
      let calculatedRateId = editModeRateId;
      if (refundRates.length > 0 && editDialog.agreement.rate) {
        const rateNum = parseDecimalValue(editDialog.agreement.rate);
        const matchingRate = refundRates.find((r) => {
          const fromRate = parseDecimalValue(r.fromRate);
          return Math.abs(fromRate - rateNum) < 0.01; // Allow small floating point differences
        });
        calculatedRateId = matchingRate ? matchingRate.id : "";
      }
      return {
        productType: editDialog.agreement.productType,
        country: editDialog.agreement.country,
        volumePeriod: editDialog.agreement.volumePeriod,
        volume: formatWithTwoDecimals(editDialog.agreement.volume),
        rate: rateValue,
        rateId: calculatedRateId || "",
        tieredPrice: formatWithTwoDecimals(editDialog.agreement.tieredPrice),
        priceAdjustmentType,
        priceAdjustmentPercent,
        vf: editDialog.agreement.vf ? formatWithTwoDecimals(editDialog.agreement.vf) : "",
        preFinancePeriodType: editDialog.agreement.preFinancePeriodType || "",
      };
    }
    if (createDialog?.draft) {
      return {
        ...initialValues,
        productType: createDialog.draft.productType || "",
        country: createDialog.draft.country || "",
        volumePeriod: createDialog.draft.volumePeriod || "",
        volume:
          createDialog.draft.volume || createDialog.draft.volume === 0
            ? formatWithTwoDecimals(createDialog.draft.volume)
            : "",
        rateId: "",
      };
    }
    return { ...initialValues, rateId: "" };
  }, [createDialog, editDialog.agreement, refundRates, editModeRateId]);

  return (
    <Card mb={0}>
      <CardContent>
        <Paper mt={0}>
          <div>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setOpen(true)}
              disabled={!canWritePricing}
            >
              Add Pricing Agreement
            </Button>
            <Dialog
              open={open}
              onClose={handleClose}
              aria-labelledby="form-dialog-title"
              maxWidth="md"
              fullWidth
            >
              <DialogTitle id="form-dialog-title">
                {editDialog.agreement ? "Edit Pricing Agreement" : "Add Pricing Agreement"}
              </DialogTitle>
              <Divider />
              <DialogContent>
                <Formik
                  initialValues={formInitialValues}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                  enableReinitialize
                >
                  {({
                    errors,
                    handleBlur,
                    handleChange,
                    handleSubmit,
                    isSubmitting,
                    touched,
                    values,
                    setFieldValue,
                  }) => {
                    // Calculate price whenever relevant fields change
                    const signedAdjustmentPercent =
                      values.priceAdjustmentType === "none"
                        ? 0
                        : (values.priceAdjustmentType === "discount" ? -1 : 1) *
                          parseDecimalValue(values.priceAdjustmentPercent);
                    const calculatedPrice = calculatePrice(values.tieredPrice, signedAdjustmentPercent);

                    // Create rate options with monthly fee information (always monthly, independent of selected period)
                    const rateOptionsWithFee = refundRates.map((rate) => {
                      const rateRange = `${formatPriceWithEuro(rate.fromRate)} - ${formatPriceWithEuro(rate.toRate)}`;
                      const monthlyFee = parseDecimalValue(rate.monthFee || 0);
                      const feeText = ` (Monthly Fee: ${formatPriceWithEuro(monthlyFee)})`;
                      
                      return {
                        key: rate.id,
                        value: `${rateRange}${feeText}`,
                        rateValue: rate.fromRate, // Store the actual rate value
                      };
                    });

                    return (
                      <Card mb={6}>
                        <CardContent>
                          {isSubmitting ? (
                            <Box display="flex" justifyContent="center" my={20} mx={40}>
                              <CircularProgress />
                            </Box>
                          ) : (
                            <form onSubmit={handleSubmit}>
                              <Grid container spacing={6}>
                                <Grid item md={12} xs={12}>
                                  <Select
                                    name="productType"
                                    label="Product"
                                    options={productOptions}
                                    required={true}
                                    onChange={(e) => {
                                      handleChange(e);
                                      const productId = e.target.value;
                                      handleProductChange(productId);
                                      setFieldValue("rate", ""); // Reset rate when product changes
                                      setFieldValue("rateId", "");
                                      setFieldValue("tieredPrice", ""); // Reset tier price
                                      if (!isCountryValidForProduct(values.country, productId)) {
                                        setFieldValue("country", "");
                                        setSelectedCountry(null);
                                      }
                                      if (parseInt(productId, 10) === EXCISE_PRODUCT_TYPE) {
                                        setFieldValue("preFinancePeriodType", PRE_FINANCE_PERIOD_MONTHLY);
                                      }
                                      // Fetch refund rates when product changes
                                      if (values.country && isCountryValidForProduct(values.country, productId)) {
                                        fetchRefundRates(values.country, productId).then(() => {
                                          // Recalculate tier price if volume and volumePeriod are set
                                          if (values.volume && values.volumePeriod) {
                                            const normalizedVolume = parseDecimalValue(values.volume);
                                            if (normalizedVolume > 0) {
                                              const matchingRate = findRateByVolume(normalizedVolume, values.volumePeriod);
                                              if (matchingRate) {
                                                setFieldValue("rate", formatWithTwoDecimals(matchingRate.fromRate));
                                                setFieldValue("rateId", matchingRate.id);
                                                const tierPrice = calculateTierPrice(values.volumePeriod, matchingRate);
                                                setFieldValue("tieredPrice", formatWithTwoDecimals(tierPrice));
                                              }
                                            }
                                          }
                                        });
                                      }
                                    }}
                                  />
                                </Grid>
                                <Grid item md={12} xs={12}>
                                  <Select
                                    name="country"
                                    label="Country (MSR)"
                                    options={countryOptions}
                                    required={true}
                                    disabled={!values.productType}
                                    helperText={getCountryProductHelperText(values.productType)}
                                    onChange={(e) => {
                                      handleChange(e);
                                      const countryId = e.target.value;
                                      handleCountryChange(countryId);
                                      setFieldValue("rate", ""); // Reset rate when country changes
                                      setFieldValue("rateId", "");
                                      setFieldValue("tieredPrice", ""); // Reset tier price
                                      // Fetch refund rates when country changes
                                      if (countryId && values.productType) {
                                        fetchRefundRates(countryId, values.productType).then(() => {
                                          // Recalculate tier price if volume and volumePeriod are set
                                          if (values.volume && values.volumePeriod) {
                                            const normalizedVolume = parseDecimalValue(values.volume);
                                            if (normalizedVolume > 0) {
                                              const matchingRate = findRateByVolume(normalizedVolume, values.volumePeriod);
                                              if (matchingRate) {
                                                setFieldValue("rate", formatWithTwoDecimals(matchingRate.fromRate));
                                                setFieldValue("rateId", matchingRate.id);
                                                const tierPrice = calculateTierPrice(values.volumePeriod, matchingRate);
                                                setFieldValue("tieredPrice", formatWithTwoDecimals(tierPrice));
                                              }
                                            }
                                          }
                                        });
                                      }
                                    }}
                                  />
                                </Grid>
                                <Grid item md={12} xs={12}>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Select
                                      name="volumePeriod"
                                      label="Volume Period"
                                      options={volumePeriodOptions}
                                      required={true}
                                      style={{ flex: 1 }}
                                      onChange={(e) => {
                                        handleChange(e);
                                        // Recalculate tier price and rate when volume period changes
                                        if (values.volume && refundRates.length > 0 && values.country && values.productType) {
                                          const normalizedVolume = parseDecimalValue(values.volume);
                                          if (normalizedVolume > 0) {
                                            const matchingRate = findRateByVolume(normalizedVolume, e.target.value);
                                            if (matchingRate) {
                                              setFieldValue("rate", formatWithTwoDecimals(matchingRate.fromRate));
                                              setFieldValue("rateId", matchingRate.id);
                                              const tierPrice = calculateTierPrice(e.target.value, matchingRate);
                                              setFieldValue("tieredPrice", formatWithTwoDecimals(tierPrice));
                                            }
                                          }
                                        }
                                      }}
                                    />
                                    <Tooltip title="1 = Month (uses MonthFee), 2 = Quarter (uses MonthFee × 3), 3 = Year (uses MonthFee × 12)">
                                      <IconButton size="small">
                                        <InfoIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </Grid>
                                <Grid item md={12} xs={12}>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <TextField
                                      name="volume"
                                      label={values.productType === 1 ? "Volume (€)" : values.productType === 2 ? "Volume (Liters)" : "Volume"}
                                      fullWidth
                                      value={values.volume}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        if (value === "" || /^[0-9]*[.,]?[0-9]*$/.test(value)) {
                                          setFieldValue("volume", value);
                                          
                                          // Auto-select rate and calculate tier price when volume changes
                                          if (value && values.country && values.productType && values.volumePeriod) {
                                            const normalizedVolume = parseDecimalValue(value);
                                            if (normalizedVolume > 0) {
                                              const matchingRate = findRateByVolume(normalizedVolume, values.volumePeriod);
                                              if (matchingRate) {
                                                // Set the rate
                                                setFieldValue("rate", formatWithTwoDecimals(matchingRate.fromRate));
                                                setFieldValue("rateId", matchingRate.id);
                                                
                                                // Calculate and set tier price
                                                const tierPrice = calculateTierPrice(values.volumePeriod, matchingRate);
                                                setFieldValue("tieredPrice", formatWithTwoDecimals(tierPrice));
                                              }
                                            }
                                          }
                                        }
                                      }}
                                      onBlur={(e) => {
                                        const normalized = parseDecimalValue(e.target.value);
                                        const formatted = formatWithTwoDecimals(normalized);
                                        setFieldValue("volume", formatted);
                                        
                                        // Auto-select rate and calculate tier price when volume is finalized
                                        if (normalized > 0 && values.country && values.productType && values.volumePeriod) {
                                          const matchingRate = findRateByVolume(normalized, values.volumePeriod);
                                          if (matchingRate) {
                                            // Set the rate
                                            setFieldValue("rate", formatWithTwoDecimals(matchingRate.fromRate));
                                            setFieldValue("rateId", matchingRate.id);
                                            
                                            // Calculate and set tier price
                                            const tierPrice = calculateTierPrice(values.volumePeriod, matchingRate);
                                            setFieldValue("tieredPrice", formatWithTwoDecimals(tierPrice));
                                          } else if (setNotify) {
                                            setNotify({
                                              isOpen: true,
                                              message: `No refund rate found for volume ${formatted}. Please check the volume range.`,
                                              type: "warning",
                                            });
                                          }
                                        }
                                      }}
                                      error={Boolean(touched.volume && errors.volume)}
                                      helperText={touched.volume && errors.volume}
                                    />
                                    <Tooltip
                                      title={
                                        values.productType === 1
                                          ? "Volume in € for VAT. Rate and Tier Price will be auto-selected based on this volume."
                                          : values.productType === 2
                                          ? "Volume in Liters for Excise. Rate and Tier Price will be auto-selected based on this volume."
                                          : "Volume. Rate and Tier Price will be auto-selected based on this volume."
                                      }
                                    >
                                      <IconButton size="small">
                                        <InfoIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </Grid>
                                <Grid item md={12} xs={12}>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    {loadingRates ? (
                                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
                                        <CircularProgress size={20} />
                                        <Typography variant="body2">Loading rates...</Typography>
                                      </Box>
                                    ) : rateOptionsWithFee.length > 0 ? (
                                      <Select
                                        name="rateId"
                                        label="Rate"
                                        options={rateOptionsWithFee}
                                        required={true}
                                        disabled={!values.country || !values.productType}
                                        style={{ flex: 1 }}
                                        value={values.rateId || findRateIdByValue(values.rate)}
                                        onChange={(e) => {
                                          setFieldValue("rateId", e.target.value);
                                          // Set the actual rate value from the selected refund rate
                                          const selectedRate = refundRates.find((r) => r.id === parseInt(e.target.value));
                                          if (selectedRate) {
                                            setFieldValue("rate", formatWithTwoDecimals(selectedRate.fromRate));
                                            // Recalculate tier price when rate is manually changed
                                            if (values.volumePeriod) {
                                              const tierPrice = calculateTierPrice(values.volumePeriod, selectedRate);
                                              setFieldValue("tieredPrice", formatWithTwoDecimals(tierPrice));
                                            }
                                          }
                                        }}
                                      />
                                    ) : values.country && values.productType ? (
                                      <TextField
                                        name="rate"
                                        label="Rate"
                                        fullWidth
                                        value={values.rate}
                                        onChange={(e) => {
                                          const value = e.target.value;
                                          if (value === "" || /^[0-9]*[.,]?[0-9]*$/.test(value)) {
                                            setFieldValue("rate", value);
                                            setFieldValue("rateId", ""); // Clear rateId when manually entering rate
                                          }
                                        }}
                                        onBlur={(e) => {
                                          const normalized = parseDecimalValue(e.target.value);
                                          const formatted = formatWithTwoDecimals(normalized);
                                          setFieldValue("rate", formatted);
                                          // Try to find matching rate ID after formatting
                                          const rateId = findRateIdByValue(formatted);
                                          if (rateId) {
                                            setFieldValue("rateId", rateId);
                                          }
                                        }}
                                        error={Boolean(touched.rate && errors.rate)}
                                        helperText={(touched.rate && errors.rate) || "No refund rates available. Enter rate manually."}
                                        style={{ flex: 1 }}
                                        InputProps={{
                                          startAdornment: <InputAdornment position="start">€</InputAdornment>,
                                        }}
                                      />
                                    ) : (
                                      <TextField
                                        name="rate"
                                        label="Rate"
                                        fullWidth
                                        disabled
                                        value=""
                                        helperText="Select Country and Product first"
                                        style={{ flex: 1 }}
                                      />
                                    )}
                                    <Tooltip title="Refund rates for the selected country and product. Automatically selected based on volume when volume is entered.">
                                      <IconButton size="small">
                                        <InfoIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </Grid>
                                <Grid item md={12} xs={12}>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <TextField
                                      name="tieredPrice"
                                      label="Tier Price"
                                      fullWidth
                                      value={values.tieredPrice}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        if (value === "" || /^[0-9]*[.,]?[0-9]*$/.test(value)) {
                                          setFieldValue("tieredPrice", value);
                                        }
                                      }}
                                      onBlur={(e) => {
                                        const normalized = parseDecimalValue(e.target.value);
                                        setFieldValue("tieredPrice", formatWithTwoDecimals(normalized));
                                      }}
                                      error={Boolean(touched.tieredPrice && errors.tieredPrice)}
                                      helperText={
                                        (touched.tieredPrice && errors.tieredPrice) ||
                                        "Automatically calculated based on Volume and Volume Period, but can be manually adjusted"
                                      }
                                      InputProps={{
                                        startAdornment: <InputAdornment position="start">€</InputAdornment>,
                                      }}
                                    />
                                    <Tooltip title="Tier Price is automatically calculated based on Volume and Volume Period (Month = MonthFee, Quarter = MonthFee × 3, Year = MonthFee × 12)">
                                      <IconButton size="small">
                                        <InfoIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </Grid>
                                <Grid item md={12} xs={12}>
                                  <FormControl component="fieldset">
                                    <FormLabel component="legend">Price Adjustment Type</FormLabel>
                                    <RadioGroup
                                      row
                                      name="priceAdjustmentType"
                                      value={values.priceAdjustmentType}
                                      onChange={(e) => {
                                        const nextType = e.target.value;
                                        setFieldValue("priceAdjustmentType", nextType);
                                        if (nextType === "none") {
                                          setFieldValue("priceAdjustmentPercent", formatWithTwoDecimals(0));
                                        }
                                      }}
                                    >
                                      <FormControlLabel value="none" control={<Radio />} label="None" />
                                      <FormControlLabel value="discount" control={<Radio />} label="Discount" />
                                      <FormControlLabel value="surcharge" control={<Radio />} label="Surcharge" />
                                    </RadioGroup>
                                  </FormControl>
                                </Grid>
                                <Grid item md={12} xs={12}>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <TextField
                                      name="priceAdjustmentPercent"
                                      label={
                                        values.priceAdjustmentType === "discount"
                                          ? "Discount (%)"
                                          : values.priceAdjustmentType === "surcharge"
                                          ? "Surcharge (%)"
                                          : "Adjustment (%)"
                                      }
                                      fullWidth
                                      value={values.priceAdjustmentPercent}
                                      disabled={values.priceAdjustmentType === "none"}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        if (value === "" || /^[0-9]*[.,]?[0-9]*$/.test(value)) {
                                          setFieldValue("priceAdjustmentPercent", value);
                                        }
                                      }}
                                      onBlur={(e) => {
                                        const normalized = parseDecimalValue(e.target.value);
                                        setFieldValue("priceAdjustmentPercent", formatWithTwoDecimals(normalized));
                                      }}
                                      error={Boolean(touched.priceAdjustmentPercent && errors.priceAdjustmentPercent)}
                                      helperText={touched.priceAdjustmentPercent && errors.priceAdjustmentPercent}
                                      InputProps={{
                                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                      }}
                                    />
                                    <Tooltip title="Adjustment percentage (0-100%)">
                                      <IconButton size="small">
                                        <InfoIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </Grid>
                                <Grid item md={12} xs={12}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 2,
                                      flexWrap: { xs: "wrap", md: "nowrap" },
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        flex: { xs: "1 1 100%", md: "0 0 280px" },
                                        minWidth: 220,
                                      }}
                                    >
                                      <TextField
                                        name="vf"
                                        label="VF (Optional) (%)"
                                        fullWidth
                                        value={values.vf}
                                        onChange={(e) => {
                                          const value = e.target.value;
                                          if (value === "" || /^[0-9]*[.,]?[0-9]*$/.test(value)) {
                                            setFieldValue("vf", value);
                                            if (!hasPositiveVf(value)) {
                                              setFieldValue("preFinancePeriodType", "");
                                            } else if (parseInt(values.productType, 10) === 2) {
                                              setFieldValue("preFinancePeriodType", PRE_FINANCE_PERIOD_MONTHLY);
                                            }
                                          }
                                        }}
                                        onBlur={(e) => {
                                          const raw = e.target.value;
                                          if (!raw) {
                                            setFieldValue("vf", "");
                                            setFieldValue("preFinancePeriodType", "");
                                            return;
                                          }

                                          const normalized = parseDecimalValue(raw);
                                          if (!hasPositiveVf(raw)) {
                                            setFieldValue("vf", "");
                                            setFieldValue("preFinancePeriodType", "");
                                            return;
                                          }

                                          setFieldValue("vf", formatWithTwoDecimals(normalized));
                                        }}
                                        error={Boolean(touched.vf && errors.vf)}
                                        helperText={touched.vf && errors.vf}
                                        InputProps={{
                                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                        }}
                                      />
                                      <Tooltip title="VF percentage (0-100%, optional)">
                                        <IconButton size="small">
                                          <InfoIcon />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                    <FormControl component="fieldset" sx={{ flex: 1, minWidth: 280 }}>
                                      <FormLabel component="legend" sx={{ mb: 0.5 }}>
                                        Pre-finance Frequency
                                      </FormLabel>
                                      <RadioGroup
                                        row
                                        name="preFinancePeriodType"
                                        value={values.preFinancePeriodType}
                                        onChange={(e) => {
                                          const nextPeriodType = parseInt(e.target.value, 10);
                                          const isExcise =
                                            parseInt(values.productType, 10) === EXCISE_PRODUCT_TYPE;

                                          if (
                                            isExcise &&
                                            nextPeriodType === PRE_FINANCE_PERIOD_QUARTERLY
                                          ) {
                                            setConfirmDialog({
                                              isOpen: true,
                                              title: "Use quarterly pre-finance for Excise?",
                                              subTitle:
                                                "Excise pre-finance normally uses Monthly (p/M). Do you want to select Quarterly (p/Q) instead?",
                                              onConfirm: () => {
                                                setFieldValue(
                                                  "preFinancePeriodType",
                                                  PRE_FINANCE_PERIOD_QUARTERLY
                                                );
                                                setConfirmDialog((current) => ({
                                                  ...current,
                                                  isOpen: false,
                                                }));
                                              },
                                            });
                                            return;
                                          }

                                          setFieldValue("preFinancePeriodType", nextPeriodType);
                                        }}
                                      >
                                        <FormControlLabel
                                          value={PRE_FINANCE_PERIOD_MONTHLY}
                                          control={<Radio />}
                                          label="Monthly (p/M)"
                                        />
                                        <FormControlLabel
                                          value={PRE_FINANCE_PERIOD_QUARTERLY}
                                          control={<Radio />}
                                          label="Quarterly (p/Q)"
                                        />
                                      </RadioGroup>
                                    </FormControl>
                                  </Box>
                                  {touched.preFinancePeriodType && errors.preFinancePeriodType ? (
                                    <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                                      {errors.preFinancePeriodType}
                                    </Typography>
                                  ) : null}
                                </Grid>
                                <Grid item md={12} xs={12}>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <TextField
                                      name="calculatedPrice"
                                      label="Calculated Price"
                                      fullWidth
                                      value={formatPriceWithEuro(calculatedPrice)}
                                      disabled
                                      InputProps={{
                                        readOnly: true,
                                      }}
                                    />
                                    <Tooltip title="Calculated Price: Tier Price × (1 + Adjustment%). This is the final price after applying discount or surcharge.">
                                      <IconButton size="small">
                                        <InfoIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </Grid>
                              </Grid>

                              <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                mt={3}
                                my={2}
                                mr={4}
                                disabled={isSubmitting}
                              >
                                {editDialog.agreement ? "Update" : "Submit"} Pricing Agreement
                              </Button>
                              <Button
                                type="button"
                                variant="contained"
                                color="primary"
                                mt={3}
                                onClick={handleClose}
                                my={2}
                              >
                                Close
                              </Button>
                            </form>
                          )}
                        </CardContent>
                      </Card>
                    );
                  }}
                </Formik>
              </DialogContent>
            </Dialog>
          </div>
        </Paper>
      </CardContent>
      <ConfirmDialog confirmDialog={confirmDialog} setConfirmDialog={setConfirmDialog} />
    </Card>
  );
};

export default AddCustomerPricingAgreement;

